// @flow

import * as React from 'react';

import { useField, useForm } from 'react-final-form';

import { Base } from '@gandi/ui';

import {
  CERT_PROVIDER_INPUT_NAME,
  CERT_TYPE_INPUT_NAME,
  CERT_TYPE_PROTECT_INPUT_NAME,
  CERT_WARRANTY_INPUT_NAME,
} from '../../consts';

import type { FormValues } from '../../types';

import { NoWarranty } from './NoWarranty';
import { NumberAddressesOption } from './NumberAddressesOption';
import { ProviderOption } from './ProviderOption';
import { TypeOption } from './TypeOption';
import { TypeProtectOption } from './TypeProtectOption';
import { WarrantyInfo } from './WarrantyInfo';
import { computeIs250kWarrantyOnly } from './helpers/computeIs250kWarrantyOnly';

type StepTypeProps = {
  readOnly?: boolean,
  process: 'create' | 'renew',
  isSHS?: boolean,
  providerAlert?: React.Node,
};
export function StepType({
  readOnly,
  process,
  isSHS,
  providerAlert,
}: StepTypeProps): React.MixedElement {
  const {
    input: { value: provider },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_PROVIDER_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: type },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_TYPE_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: protect },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_TYPE_PROTECT_INPUT_NAME, { subscription: { value: true } });
  const {
    input: { value: warranty },
    // $FlowFixMe[prop-missing]
  } = useField(CERT_WARRANTY_INPUT_NAME, { subscription: { value: true } });

  const form = useForm<FormValues>();

  // handle provider change and fix warranty
  React.useEffect(() => {
    if (provider === 'sectigo' && type === 'pro' && warranty !== '250000') {
      form.change(CERT_WARRANTY_INPUT_NAME, '250000');
    }
  }, [provider, type, warranty, form]);

  const is250kWarrantyOnly = computeIs250kWarrantyOnly({ type, protect });

  // for sectigo standard no warranty, display for all others
  const showNoWarranty =
    !is250kWarrantyOnly && type === 'standard' && provider === 'sectigo';

  const showWarrantyInfo = !showNoWarranty;

  // Number addresses selection only when multi addresses is selected
  // Note: if the field is not shown it will not be in the form values in the end.
  const showNumberAddressesOption = protect === 'multi';

  return (
    <>
      <Base marginBottom="base">
        <ProviderOption readOnly={readOnly} providerAlert={providerAlert} />
      </Base>
      <Base marginBottom="base">
        <TypeOption readOnly={readOnly} />
      </Base>
      <TypeProtectOption readOnly={readOnly} process={process} isSHS={isSHS} />
      {showNumberAddressesOption && (
        <Base marginTop="base">
          <NumberAddressesOption readOnly={readOnly} process={process} />
        </Base>
      )}
      {showWarrantyInfo && (
        <Base marginTop="base">
          <WarrantyInfo />
        </Base>
      )}
      {showNoWarranty && (
        <Base marginTop="base">
          <NoWarranty />
        </Base>
      )}
    </>
  );
}
